import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Trash2, Eye, EyeOff, Loader2 } from "lucide-react";

interface ProgressPhoto {
  id: string;
  storage_path: string;
  photo_type: string;
  photo_date: string;
  caption: string | null;
  is_visible_to_client: boolean;
}

interface PhotoCardProps {
  photo: ProgressPhoto;
  onDelete?: (photoId: string) => void;
  onToggleVisibility?: (photoId: string, visible: boolean) => void;
  onSelect?: (photo: ProgressPhoto) => void;
  isSelected?: boolean;
  showActions?: boolean;
}

const photoTypeLabels: Record<string, string> = {
  front: "Front",
  side: "Side",
  back: "Back",
  other: "Other",
};

const PhotoCard = ({
  photo,
  onDelete,
  onToggleVisibility,
  onSelect,
  isSelected = false,
  showActions = true,
}: PhotoCardProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);

        const { data, error: signedUrlError } = await supabase.storage
          .from("progress-photos")
          .createSignedUrl(photo.storage_path, 3600); // 1 hour expiry

        if (signedUrlError) throw signedUrlError;

        setImageUrl(data.signedUrl);
      } catch (err) {
        console.error("Error loading image:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [photo.storage_path]);

  return (
    <Card
      className={`overflow-hidden cursor-pointer transition-all ${
        isSelected ? "ring-2 ring-primary" : ""
      } ${onSelect ? "hover:ring-2 hover:ring-primary/50" : ""}`}
      onClick={() => onSelect?.(photo)}
    >
      <div className="aspect-[3/4] relative bg-muted">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Failed to load</p>
          </div>
        ) : (
          <img
            src={imageUrl || ""}
            alt={`${photo.photo_type} progress photo`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Overlay badges */}
        <div className="absolute top-2 left-2 flex gap-1">
          <Badge variant="secondary" className="text-xs">
            {photoTypeLabels[photo.photo_type] || photo.photo_type}
          </Badge>
        </div>

        {!photo.is_visible_to_client && (
          <div className="absolute top-2 right-2">
            <Badge variant="outline" className="text-xs bg-background/80">
              <EyeOff className="h-3 w-3 mr-1" />
              Hidden
            </Badge>
          </div>
        )}
      </div>

      <div className="p-3">
        <p className="text-sm font-medium">
          {format(new Date(photo.photo_date), "MMM d, yyyy")}
        </p>
        {photo.caption && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {photo.caption}
          </p>
        )}

        {showActions && (onDelete || onToggleVisibility) && (
          <div className="flex gap-1 mt-2">
            {onToggleVisibility && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleVisibility(photo.id, !photo.is_visible_to_client);
                }}
              >
                {photo.is_visible_to_client ? (
                  <>
                    <EyeOff className="h-3 w-3 mr-1" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="h-3 w-3 mr-1" />
                    Show
                  </>
                )}
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(photo.id);
                }}
              >
                <Trash2 className="h-3 w-3 mr-1 text-destructive" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PhotoCard;
